import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Toast, ToastContainer } from "react-bootstrap";
import api, { dedupedRequest, invalidateCacheByUrl } from "../../../utils/axios";
import { toYMD } from '../../../utils/utils'
// import { toDE, toYMD } from '../../../utils/utils'


type TaskCategory = {
  id: number;
  name: string;
  selected: boolean;
};

type TaskPayload = {
  // id: number;
  origin: number | 0;
  note?: string | null;
  suggestion?: string | null;
  client_law_part_id?: number | null;
  deadline?: string | null;
  responsible?: string | null;
  origin_link?: string | null;
  approval?: boolean;
  approval_date?: string | null;
  approval_person?: string | null;
  comment?: string | null;
  done?: boolean;
  done_date?: string | null;
  done_person?: string | null;
  effective_date?: string | null;
  effective_person?: string | null;
  //  created_user_id = db.Column(db.SmallInteger)
  //  created_timestamp = db.Column(db.DateTime)
  //  update_user_id = db.Column(db.SmallInteger)
  //  update_timestamp = db.Column(db.DateTime)
  //  client_id = db.Column(db.Integer, nullable=False)
  recurring_months?: number | null;
  escalation_needed?: boolean;
  escalation1_person?: string | null;
  escalation2_person?: string | null;
  escalation3_person?: string | null;
  task_categories?: number[]; // <-- IDs only
  files?: any[];
  number_of_files?: number;
  reminder_enabled?: boolean;
  reminder_recipients?: string | null;
  evidence_required?: boolean;
  evidence_provided?: boolean;
};

// Internal form state used by the UI:
type FormState = {
  id?: number;          // ID is only for the UI
  task_categories?: TaskCategory[];
} & Partial<Omit<TaskPayload, "task_categories">>;

interface TaskModalProps {
  show: boolean;
  TaskId: number;
  t: any;
  onClose: () => void;
  onSave: () => void;
  law_part_id: number;
}

const TaskModal: React.FC<TaskModalProps> = ({
  show,
  TaskId,
  onClose,
  onSave,
  law_part_id
}) => {
  // interface FieldChangeEvent extends React.ChangeEvent<HTMLInputElement> {}
  const [form, setForm] = useState<FormState>({ task_categories: [] });
  const [taskFileDescription, setTaskFileDescription] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // const [error, setError] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File|null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [uploadSuccessMessage, setUploadSuccessMessage] = useState<string>("");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  const uploadOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target;

    if (name === 'description') {
      setTaskFileDescription(value);
      return;
    }

    const f = files?.[0] || null;
    setUploadFile(f || null);
  };

  const onUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('description', taskFileDescription || '');
      const { data } = await api.post(`/task/${TaskId}/doc`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      console.log("Upload response data:", data);
      setUploadSuccessMessage(`${uploadFile.name} wurde erfolgreich hochgeladen.`);
      setUploadSuccess(true);
      setUploadFile(null);
      setTaskFileDescription('');
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Der Datei-Upload ist fehlgeschlagen.';
      setUploadError(message);
      setUploadSuccess(false);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (TaskId === undefined || TaskId === null || TaskId == 0) return;
    if (TaskId > -1) {
      loadTask(TaskId);
    }

    loadAllTaskCategories();
  }, [TaskId]);

  // Seed from the task's existing categories: mark them selected
  const seedFromTask = (taskCategories: Array<{ id: number; name: string }> = [],): TaskCategory[] =>
    taskCategories.map((tc) => ({ id: tc.id, name: tc.name, selected: true }));

  // Merge available categories with a set of selected IDs
  const mergeAvailable = (
    available: Array<{ id: number; name: string }>,
    selectedIds: Set<number>,
  ): TaskCategory[] =>
    available.map((a) => ({
      id: a.id,
      name: a.name,
      selected: selectedIds.has(a.id),
    }));

  const loadTask = async (taskId: number) => {
    try {
      const res = await dedupedRequest(
        { method: "GET", url: `/task/${taskId}` },
        { useCache: false },
      );
      const task = res.data;

      setForm((prev) => ({
        ...prev,
        id: task.id,
        origin: task.origin,
        note: task.note,
        suggestion: task.suggestion,
        client_law_part_id: task.client_law_part_id,
        deadline: toYMD(task.deadline),
        responsible: task.responsible,
        origin_link: task.origin_link,
        approval: task.approval,
        approval_date: task.approval_date,
        approval_person: task.approval_person,
        comment: task.comment,
        done: task.done,
        done_date: task.done_date,
        done_person: task.done_person,
        effective_date: task.effective_date,
        effective_person: task.effective_person,
        recurring_months: task.recurring_months,
        escalation_needed: task.escalation_needed,
        escalation1_person: task.escalation1_person,
        escalation2_person: task.escalation2_person,
        escalation3_person: task.escalation3_person,
        task_categories: seedFromTask(task.task_categories ?? []),
        number_of_files: task.number_of_files,
        task_files: task.task_files,
        reminder_enabled: task.reminder_enabled,
        reminder_recipients: task.reminder_recipients,
        evidence_required: Boolean(task.evidence_required),
        evidence_provided: Boolean(task.evidence_provided),
      }));
    } catch (err) {
      console.error("Failed to load task", err);
    }
  };

  const loadAllTaskCategories = async () => {
    try {
      const res = await dedupedRequest(
        { method: "GET", url: "/task_categories" },
        { useCache: false },
      );
      const available: Array<{ id: number; name: string }> = res.data;

      setForm((prev) => {
        const selectedIds = new Set(
          (prev.task_categories ?? [])
            .filter((t) => t.selected)
            .map((t) => t.id),
        );
        return {
          ...prev,
          task_categories: mergeAvailable(available, selectedIds),
        };
      });
    } catch (err) {
      console.error("Failed to load task categories", err);
    }
  };

  const updateField =
    (key: keyof TaskPayload) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let value: any;
      // console.log("Updating field", key, "type:", e.target.type, "to", e.target.value);
      if (e.target.type === "checkbox") {
        value = (e.target as HTMLInputElement).checked;
      } else if (e.target.type === "number") {
        value =
          e.target.value === ""
            ? null
            : Number(e.target.value);
      } else {
        value = e.target.value;
      }
      setForm((prev) => ({ ...prev, [key]: value }));
    };

  const isValidDate = (value: string) => {
    if (value) {
      const isValidDateStr = /^\d{4}-\d{2}-\d{2}$/.test(value);
      const year = value.split('-')[0];
      const yearIsValid = year.length === 4 && parseInt(year) >= 1900 && parseInt(year) <= 2100;
      return isValidDateStr && yearIsValid;
    }
    return false; // don'tallow empty value
  }

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (!value) {
          setForm({ ...form, deadline: null });
          return;
      }
      if (isValidDate(value) || value.length <= 10) {
          setForm({ ...form, deadline: value });
      }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    // setError(null);

    if (form.deadline) {
      if (!isValidDate(form.deadline)) {
        // setError("Das Fälligkeitsdatum hat ein ungültiges Format. Bitte verwenden Sie das Format YYYY-MM-DD (z.B. 2026-05-04).");
        setSaving(false);
        return;
      }
    }

    // Only send fields the user actually filled
    const { task_categories, ...rest } = form;
    const payload: TaskPayload = {
      ...rest,
      origin: rest.origin ?? law_part_id, // For new tasks, set origin to the current law part  
      done: !!rest.done, // Ensure done is a boolean
      task_categories: (task_categories ?? [])
        .filter((t) => t.selected)
        .map((t) => t.id), // <-- only IDs
    };

    const method = (TaskId === -1) ? "POST" : "PUT";
    const url = (TaskId === -1) ? `/tasks/${law_part_id}` : `/tasks/${TaskId}`;

    try {
      await dedupedRequest(
        { method: method, url: url, data: payload },
        { useCache: false, maxRetries: 2 },
      );
      // Bust cached GET /admin/clients so the list refreshes correctly
      invalidateCacheByUrl("/tasks");
      onSave(); // triggers parent to reload
      onClose(); // close the modal
    } catch (err: any) {
      // const msg =
      //   err?.response?.data?.message ||
      //   (Array.isArray(err?.response?.data?.errors) &&
      //     "Validation error") ||
      //   "Speichern fehlgeschlagen.";
      // setError(msg);
      console.error("Error saving task:", err);
    };
    setSaving(false);
  }

  // console.log(c);

  return (
    <Modal 
      show={show} 
      onHide={onClose}
      backdrop
      className="stacked-modal"
      backdropClassName="stacked-modal-backdrop"
      >
      <Modal.Header closeButton>
        {TaskId === -1 ? <Modal.Title>Neue Maßnahme</Modal.Title> : <Modal.Title>Maßnahme bearbeiten</Modal.Title>}
      </Modal.Header>
      <Form onSubmit={handleSave} name="task_form">
        <Modal.Body>
          <Form.Group>
            <Form.Label>Handlungsbedarf <span className="text-danger">*</span></Form.Label>
            <Form.Control
              value={form.note ?? ""}
              onChange={updateField("note")}
              as="textarea"
              required
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Umsetzungsvorschlag</Form.Label>
            <Form.Control
              value={form.suggestion ?? ""}
              name="suggestion"
              onChange={updateField("suggestion")}
              as="textarea"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Durchführung</Form.Label>
            <Form.Control
              value={form.responsible ?? ""}
              name="responsible"
              onChange={updateField("responsible")}
              placeholder="Verantwortliche(r)"
            />
            <div className="grid-container">
              <Form.Control 
                value={toYMD(form.deadline)} 
                name="deadline" 
                type="date"
                onChange={handleDateChange}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  if (value &&!isValidDate(value)) {
                    setForm({ ...form, deadline: null });
                  }
                }
              }
              placeholder="Fälligkeitsdatum"
              />
              {/* <Form.Label>Erledigt</Form.Label> */}
              <Form.Check
                checked={!!form.done}
                onChange={updateField("done")}
                type="checkbox"
                label="Erledigt"
              />
            </div>
          </Form.Group>
          {/* form.deadline && (
          <Form.Group>
            <Form.Label>Erinnerungen</Form.Label>
            <Form.Check
              checked={!!form.reminder_enabled}
              onChange={updateField("reminder_enabled")}
              type="checkbox"
              label="Erinnerungen aktivieren"
            />
            <Form.Control
              value={form.reminder_recipients ?? ""}
              name="reminder_recipients"
              onChange={updateField("reminder_recipients")}
              placeholder="Empfänger E-Mail-Addressen (kommagetrennt)"
            />
          </Form.Group>
          ) */}
          <Form.Group>
            <Form.Label>Nachweise</Form.Label>
            <Form.Check
              checked={!!form.evidence_required}
              onChange={(e) => {
                const required = e.target.checked;
                setForm((prev) => ({
                  ...prev,
                  evidence_required: required,
                  evidence_provided: required ? prev.evidence_provided ?? false : false,
                }));
              }}
              type="checkbox"
              label="Nachweise erforderlich"
            />
            <Form.Check
              checked={!!form.evidence_provided}
              disabled={!form.evidence_required}
              onChange={(e) => {
                if (!form.evidence_required) return;
                setForm((prev) => ({ ...prev, evidence_provided: e.target.checked }));
              }}
              type="checkbox"
              label="Nachweise erbracht"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Anmerkungen</Form.Label>
            <Form.Control
              // as="textarea"
              value={form.comment ?? ""}
              onChange={updateField("comment")}
              as="textarea"
            />
          </Form.Group>
          { form.id && form.id > -1 &&  (
          <>
            <hr />
            <div>
              <h5>Datei hochladen:</h5>
              <Form.Group>
                <Form.Label>Beschreibung</Form.Label>
                <Form.Control
                  id="description"
                  type="text"
                  name='description'
                  value={taskFileDescription ?? ''}
                  onChange={uploadOnChange}
                />
              </Form.Group>
              <Form.Group controlId="DocFile">
                <Form.Label>Datei</Form.Label>
                <Form.Control type="file" onChange={uploadOnChange}/>
              </Form.Group>
              <div className="mt-2">
                <Button
                  type="button"
                  disabled={!uploadFile || uploading}
                  onClick={onUploadSubmit}
                >
                  {uploading ? 'Wird hochgeladen...' : 'Hochladen'}
                </Button>
              </div>
            </div>
          </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" animation="border" />{" "}
                Speichern…
              </>
            ) : (
              "Speichern"
            )}
          </Button>
        </Modal.Footer>
      </Form>
      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={uploadSuccess}
          onClose={() => setUploadSuccess(false)}
          delay={3000}
          autohide
          bg="success"
        >
          <Toast.Header>
            <strong className="me-auto">Upload erfolgreich</strong>
          </Toast.Header>
          <Toast.Body>{uploadSuccessMessage}</Toast.Body>
        </Toast>

        <Toast
          show={!!uploadError}
          onClose={() => setUploadError(null)}
          delay={4000}
          autohide
          bg="danger"
        >
          <Toast.Header>
            <strong className="me-auto">Upload fehlgeschlagen</strong>
          </Toast.Header>
          <Toast.Body>{uploadError}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Modal>
  );
};

export default TaskModal;
